import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { ApiRequestService } from '../shared/api-request.service';
import { BackgroundExportService } from '../shared/background-export.service';
import { UtilsService } from '../shared/utils.service';
import { CustomDataSource } from '../utils/custom-data-source';
import { ActionModel } from './action.model';
import { ActionsService } from './actions.service';
import { ActionsEditComponent } from './actions-edit/actions-edit.component';
import { ActionsViewTabsComponent } from './actions-view-tabs/actions-view-tabs.component';
import { ActionsFilterComponent } from './actions-filter/actions-filter.component';
import { ActionsUploadDialogComponent } from './actions-upload-dialog/actions-upload-dialog.component';
import { UserPublicProfileComponent } from '../shared/user-public-profile/user-public-profile.component';
import { ChartsUtilsService } from '../charts/charts-utils.service';
import { RBACService } from '../shared/rbac.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-actions',
  templateUrl: './actions.component.html',
  styleUrls: ['./actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActionsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new ActionsDataSource(this.app, this.api);

  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  displayedColumns: string[] = [];

  // Active export polling intervals keyed by export_id. Each entry owns its
  // own setInterval handle so concurrent exports poll independently and can
  // all be cancelled in ngOnDestroy. See .kiro/specs/task-exports-fix/design.md
  // (Defect 4, tasks 11.1 and 11.5).

  constructor(
    private actionsService: ActionsService,
    public utils: UtilsService,
    private app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    private backgroundExports: BackgroundExportService,
    public chartsUtils: ChartsUtilsService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TASKS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TASKS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TASKS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TASKS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access tasks.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [
      'id',
      'name',
      'origin',
      'type',
      'description',
      'users',
      'priority',
      'completed',
      'date_created',
      'due'
    ];
    if (this.canView || this.canDelete) {
      this.displayedColumns.unshift('select');
    }
    if (this.canView || this.canEdit || this.canDelete) {
      this.displayedColumns.push('buttons');
    }

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.dataSource.getData(false);
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.dataSource.sort_by = 'date_created';
      } else if (this.sort.active === 'due_UTC') {
        this.dataSource.sort_by = 'due';
      } else {
        this.dataSource.sort_by = this.sort.active;
      }

      this.dataSource.getData(false);
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  add() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create tasks.'
      );
      return;
    }
    this.openDialog(new ActionModel());
  }

  edit(action: ActionModel) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit tasks.'
      );
      return;
    }
    this.openDialog(action);
  }

  private openDialog(action: ActionModel) {
    this.utils
      .showComponentDialog(
        ActionsViewTabsComponent,
        {
          action: action,
          is_edit: true,
          is_system_origin: false
        },
        {
          width: '1200px'
        }
      )
      .then(() => {
        if (this.canView) {
          this.dataSource.getData();
        }
      });
  }

  view(action: ActionModel) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view tasks.'
      );
      return;
    }
    this.utils.showComponentDialog(
      ActionsViewTabsComponent,
      {
        action: action,
        is_edit: false,
        is_system_origin: false
      },
      {
        width: '1200px'
      }
    );
  }

  update(action: ActionModel) {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit tasks.'
      );
      return;
    }
    action.completed
      ? this.actionsService.complete(action.id).subscribe(() => {
          this.utils.showToast(`Completed task: "${action.name}"`);
          this.dataSource.getData();
        })
      : this.actionsService.open(action.id).subscribe(() => {
          this.utils.showToast(`Opened task: "${action.name}"`);
          this.dataSource.getData();
        });
  }

  onRemoveSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete tasks.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Tasks',
      `Are you sure you want to remove ${
        this.dataSource.selection.selected.length
      } tasks?`,
      () => {
        this.remove(this.dataSource.selection.selected);
      }
    );
  }

  onRemove(action: ActionModel) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete tasks.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Task',
      `Are you sure you want to remove "${action.name}"?`,
      () => {
        this.remove([action.id]);
      }
    );
  }

  private remove(ids: number[]) {
    this.actionsService.remove(ids).subscribe(() => {
      this.utils.showToast('Tasks have been removed');
      this.dataSource.selection.clear();
      this.dataSource.getData(false);
    });
  }

  onFilter() {
    this.utils.showComponentDialog(
      ActionsFilterComponent,
      {
        site_ids: this.dataSource.site_ids,
        assigned_users: this.dataSource.user_ids,
        priority: this.dataSource.priority,
        status: this.dataSource.status,
        origin: this.dataSource.origin,
        type: this.dataSource.type,
        date_range: this.dataSource.due_date
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.site_ids = results.site_ids
            ? results.site_ids
            : this.dataSource.site_ids;
          this.dataSource.user_ids = results.assigned_users
            ? results.assigned_users
            : this.dataSource.user_ids;
          this.dataSource.priority =
            typeof results.priority != 'undefined'
              ? results.priority
              : this.dataSource.priority;
          this.dataSource.status =
            typeof results.status != 'undefined'
              ? results.status
              : this.dataSource.status;
          this.dataSource.origin =
            typeof results.origin != 'undefined'
              ? results.origin
              : this.dataSource.origin;
          this.dataSource.type =
            typeof results.type != 'undefined'
              ? results.type
              : this.dataSource.type;
          this.dataSource.due_date = results.date_range
            ? results.date_range
            : this.dataSource.due_date;
          this.dataSource.getData();
        }
      }
    );
  }

  onUpload() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to import tasks.'
      );
      return;
    }
    this.utils.showComponentDialog(
      ActionsUploadDialogComponent,
      {},
      { width: '1440px' },
      (results) => {}
    );
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

  /**
   * Export the all or the selected actions to a PDF.
   * @param type The type of export. This can be pdf, csv or xlsx.
   * @param id The id of the task to export.
   */
  onExport(type: string = 'pdf', id?: number) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to export tasks.'
      );
      return;
    }
    // Get the selected ids.
    const ids: number[] = id ? [id] : this.dataSource.selection.selected;
    // Make the request to the API.
    this.api
      .makeRequest(
        'get',
        `v1/actions/export/${type}` +
          (ids.length > 0 ? '/' + ids.join(',') : ''),
        {},
        {
          searchBy: this.dataSource.searchBy,
          search: this.dataSource.search,
          site_ids: this.dataSource.site_ids.length
            ? this.dataSource.site_ids.join(',')
            : '',
          user_ids: this.dataSource.user_ids.length
            ? this.dataSource.user_ids.join(',')
            : '',
          priority: this.dataSource.priority ? this.dataSource.priority : '',
          origin: this.dataSource.origin ? this.dataSource.origin : '',
          type: this.dataSource.type ? this.dataSource.type : '',
          status: this.dataSource.status ? this.dataSource.status : '',
          due_date_range: this.dataSource.due_date_range
        }
      )
      .then((response) => {
        // CSV, XLSX, and PDF all return an export_id so the frontend can
        // poll via the generic BackgroundExportService and auto-download
        // when the job completes. Polling survives navigation by design:
        // the BackgroundExportService is a root singleton, so the user
        // will receive their auto-download even if they leave the Tasks
        // page.
        if (response && response.export_id) {
          this.backgroundExports.track(response.export_id);
          return;
        }
        this.utils.showToast(
          response.message ||
            'Your export is queued in the background. You will receive an email when it is ready.'
        );
      });
  }
}

export class ActionsDataSource extends CustomDataSource {
  sort_by = 'name';
  sort_order = 'asc';
  searchBy = 'name';

  // Filter Variables
  site_ids = [];
  user_ids = [];
  priority = '';
  status = '';
  origin = '';
  type = '';
  due_date: Date[] = [];

  due_date_range = '';

  getData(resetOffset = false) {
    this.due_date_range =
      this.due_date && this.due_date.length > 1
        ? [
            this.due_date[0].getTime() / 1000,
            this.due_date[1].getTime() / 1000
          ].join(',')
        : '';

    this.getDataFromLaravelAPI(`actions`, resetOffset, () => {}, {
      searchBy: this.searchBy,
      site_ids: this.site_ids.length ? this.site_ids.join(',') : '',
      user_ids: this.user_ids.length ? this.user_ids.join(',') : '',
      priority: this.priority ? this.priority : '',
      status: this.status ? this.status : '',
      origin: this.origin ? this.origin : '',
      type: this.type ? this.type : '',
      due_date_range: this.due_date_range
    });
  }
}
